// Founder's Office OS shared UI helpers — neutral palette: color is reserved for critical/warning states only.
const RED = "#FF453A";
const AMBER = "#F97316";
const NEUTRAL = "#525252";
const FAINT = "#A3A3A3";
const DARK = "#0A0A0A";
const NEUTRAL_BG = "#F5F5F5";
const RED_BG = "rgba(255,69,58,0.10)";
const AMBER_BG = "rgba(249,115,22,0.10)";

export const STATUS_COLORS = {
  active: { dot: DARK, label: "Active", bg: NEUTRAL_BG, text: DARK },
  blocked: { dot: RED, label: "Blocked", bg: RED_BG, text: RED },
  delayed: { dot: AMBER, label: "Delayed", bg: AMBER_BG, text: AMBER },
  on_hold: { dot: FAINT, label: "On Hold", bg: NEUTRAL_BG, text: NEUTRAL },
  complete: { dot: NEUTRAL, label: "Complete", bg: NEUTRAL_BG, text: NEUTRAL },
};

export const COMMITMENT_STATUS = {
  open: { label: "On Track", bg: NEUTRAL_BG, text: NEUTRAL },
  at_risk: { label: "At Risk", bg: AMBER_BG, text: AMBER },
  overdue: { label: "Overdue", bg: RED_BG, text: RED },
  done: { label: "Done", bg: NEUTRAL_BG, text: FAINT },
};

export const PRIORITY_COLORS = {
  low: FAINT,
  medium: NEUTRAL,
  high: AMBER,
  critical: RED,
};

export const RELATION_COLORS = {
  client: { bg: NEUTRAL_BG, text: DARK },
  team: { bg: NEUTRAL_BG, text: NEUTRAL },
  investor: { bg: NEUTRAL_BG, text: DARK },
  vendor: { bg: NEUTRAL_BG, text: NEUTRAL },
  partner: { bg: NEUTRAL_BG, text: NEUTRAL },
  founder: { bg: RED_BG, text: RED },
};

export const CHANNEL_ICONS = {
  // Kept as neutral single-char fallback; UI should prefer <ChannelIcon /> from BrandIcons.
  gmail: "",
  slack: "",
  teams: "",
  whatsapp: "",
  zoom: "",
  calendar: "",
  email: "",
  manual: "",
};

// Gravatar URL from an email (works across most professional inboxes).
// Uses a deterministic MD5 (WebCrypto SHA-256 fallback would need async — MD5 kept sync via lightweight impl).
function md5(str) {
  // Minimal MD5 implementation for Gravatar hashing. Adapted from RFC 1321 pseudocode.
  function rh(n) { return ("0" + (n >>> 4).toString(16)).slice(-1) + ("0" + (n & 15).toString(16)).slice(-1); }
  function ra(n) { let s = ""; for (let j = 0; j <= 3; j++) s += rh((n >>> (j * 8 + 4)) & 15) + rh((n >>> (j * 8)) & 15); return s; }
  function add(x, y) { const l = (x & 0xffff) + (y & 0xffff); return (((x >> 16) + (y >> 16) + (l >> 16)) << 16) | (l & 0xffff); }
  function rol(n, c) { return (n << c) | (n >>> (32 - c)); }
  function cmn(q, a, b, x, s, t) { return add(rol(add(add(a, q), add(x, t)), s), b); }
  function ff(a, b, c, d, x, s, t) { return cmn((b & c) | ((~b) & d), a, b, x, s, t); }
  function gg(a, b, c, d, x, s, t) { return cmn((b & d) | (c & (~d)), a, b, x, s, t); }
  function hh(a, b, c, d, x, s, t) { return cmn(b ^ c ^ d, a, b, x, s, t); }
  function ii(a, b, c, d, x, s, t) { return cmn(c ^ (b | (~d)), a, b, x, s, t); }
  function toBytes(s) { const b = []; for (let i = 0; i < s.length; i++) b.push(s.charCodeAt(i) & 0xff); return b; }
  const bytes = toBytes(str);
  const n = bytes.length;
  const w = new Array(((n + 8) >> 6) * 16 + 16).fill(0);
  for (let i = 0; i < n; i++) w[i >> 2] |= bytes[i] << ((i % 4) * 8);
  w[n >> 2] |= 0x80 << ((n % 4) * 8);
  w[w.length - 2] = n * 8;
  let a = 1732584193, b = -271733879, c = -1732584194, d = 271733878;
  for (let i = 0; i < w.length; i += 16) {
    const oa = a, ob = b, oc = c, od = d;
    a = ff(a, b, c, d, w[i], 7, -680876936); d = ff(d, a, b, c, w[i + 1], 12, -389564586); c = ff(c, d, a, b, w[i + 2], 17, 606105819); b = ff(b, c, d, a, w[i + 3], 22, -1044525330);
    a = ff(a, b, c, d, w[i + 4], 7, -176418897); d = ff(d, a, b, c, w[i + 5], 12, 1200080426); c = ff(c, d, a, b, w[i + 6], 17, -1473231341); b = ff(b, c, d, a, w[i + 7], 22, -45705983);
    a = ff(a, b, c, d, w[i + 8], 7, 1770035416); d = ff(d, a, b, c, w[i + 9], 12, -1958414417); c = ff(c, d, a, b, w[i + 10], 17, -42063); b = ff(b, c, d, a, w[i + 11], 22, -1990404162);
    a = ff(a, b, c, d, w[i + 12], 7, 1804603682); d = ff(d, a, b, c, w[i + 13], 12, -40341101); c = ff(c, d, a, b, w[i + 14], 17, -1502002290); b = ff(b, c, d, a, w[i + 15], 22, 1236535329);
    a = gg(a, b, c, d, w[i + 1], 5, -165796510); d = gg(d, a, b, c, w[i + 6], 9, -1069501632); c = gg(c, d, a, b, w[i + 11], 14, 643717713); b = gg(b, c, d, a, w[i], 20, -373897302);
    a = gg(a, b, c, d, w[i + 5], 5, -701558691); d = gg(d, a, b, c, w[i + 10], 9, 38016083); c = gg(c, d, a, b, w[i + 15], 14, -660478335); b = gg(b, c, d, a, w[i + 4], 20, -405537848);
    a = gg(a, b, c, d, w[i + 9], 5, 568446438); d = gg(d, a, b, c, w[i + 14], 9, -1019803690); c = gg(c, d, a, b, w[i + 3], 14, -187363961); b = gg(b, c, d, a, w[i + 8], 20, 1163531501);
    a = gg(a, b, c, d, w[i + 13], 5, -1444681467); d = gg(d, a, b, c, w[i + 2], 9, -51403784); c = gg(c, d, a, b, w[i + 7], 14, 1735328473); b = gg(b, c, d, a, w[i + 12], 20, -1926607734);
    a = hh(a, b, c, d, w[i + 5], 4, -378558); d = hh(d, a, b, c, w[i + 8], 11, -2022574463); c = hh(c, d, a, b, w[i + 11], 16, 1839030562); b = hh(b, c, d, a, w[i + 14], 23, -35309556);
    a = hh(a, b, c, d, w[i + 1], 4, -1530992060); d = hh(d, a, b, c, w[i + 4], 11, 1272893353); c = hh(c, d, a, b, w[i + 7], 16, -155497632); b = hh(b, c, d, a, w[i + 10], 23, -1094730640);
    a = hh(a, b, c, d, w[i + 13], 4, 681279174); d = hh(d, a, b, c, w[i], 11, -358537222); c = hh(c, d, a, b, w[i + 3], 16, -722521979); b = hh(b, c, d, a, w[i + 6], 23, 76029189);
    a = hh(a, b, c, d, w[i + 9], 4, -640364487); d = hh(d, a, b, c, w[i + 12], 11, -421815835); c = hh(c, d, a, b, w[i + 15], 16, 530742520); b = hh(b, c, d, a, w[i + 2], 23, -995338651);
    a = ii(a, b, c, d, w[i], 6, -198630844); d = ii(d, a, b, c, w[i + 7], 10, 1126891415); c = ii(c, d, a, b, w[i + 14], 15, -1416354905); b = ii(b, c, d, a, w[i + 5], 21, -57434055);
    a = ii(a, b, c, d, w[i + 12], 6, 1700485571); d = ii(d, a, b, c, w[i + 3], 10, -1894986606); c = ii(c, d, a, b, w[i + 10], 15, -1051523); b = ii(b, c, d, a, w[i + 1], 21, -2054922799);
    a = ii(a, b, c, d, w[i + 8], 6, 1873313359); d = ii(d, a, b, c, w[i + 15], 10, -30611744); c = ii(c, d, a, b, w[i + 6], 15, -1560198380); b = ii(b, c, d, a, w[i + 13], 21, 1309151649);
    a = ii(a, b, c, d, w[i + 4], 6, -145523070); d = ii(d, a, b, c, w[i + 11], 10, -1120210379); c = ii(c, d, a, b, w[i + 2], 15, 718787259); b = ii(b, c, d, a, w[i + 9], 21, -343485551);
    a = add(a, oa); b = add(b, ob); c = add(c, oc); d = add(d, od);
  }
  return ra(a) + ra(b) + ra(c) + ra(d);
}

export function gravatarUrl(email, size = 96) {
  if (!email) return null;
  const hash = md5(email.trim().toLowerCase());
  // d=404 → returns 404 when no gravatar exists (so <img onError> can fall back to initials)
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=404`;
}

export const CATEGORY_COLORS = {
  hr: { bg: NEUTRAL_BG, text: NEUTRAL },
  client: { bg: NEUTRAL_BG, text: DARK },
  ops: { bg: NEUTRAL_BG, text: NEUTRAL },
  finance: { bg: NEUTRAL_BG, text: NEUTRAL },
  legal: { bg: NEUTRAL_BG, text: NEUTRAL },
  custom: { bg: NEUTRAL_BG, text: FAINT },
};

export function healthColor(score) {
  if (score >= 70) return "#0A0A0A";
  if (score >= 40) return AMBER;
  return RED;
}

export function daysSince(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
    return Math.round(diff);
  } catch (e) {
    return null;
  }
}

export function relTime(iso) {
  if (!iso) return "—";
  const d = daysSince(iso);
  if (d === null) return "—";
  if (d === 0) return "today";
  if (d === 1) return "1d ago";
  if (d < 0) return `in ${Math.abs(d)}d`;
  return `${d}d ago`;
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch (e) {
    return "—";
  }
}

export function contactColor(iso) {
  const d = daysSince(iso);
  if (d === null) return NEUTRAL;
  if (d <= 7) return NEUTRAL;
  if (d <= 14) return AMBER;
  return RED;
}

export function initials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");
}
